// src/core/query/useSqlAutocomplete.ts
import { useEffect, useState } from "react";
import { sql as sqlLang } from "@codemirror/lang-sql";
import {
  autocompletion,
  completeFromList,
  type CompletionContext,
  type CompletionResult,
} from "@codemirror/autocomplete";
import type { AsyncDuckDBConnection } from "@duckdb/duckdb-wasm";
import { listRelations, type SQLToken } from "@/lib";

// A small set of SQL keywords for suggestions
const KEYWORDS = [
  "SELECT","FROM","WHERE","JOIN","ON","GROUP","BY","HAVING","ORDER","LIMIT",
  "INSERT","INTO","VALUES","UPDATE","SET","DELETE","CREATE","TABLE","VIEW",
  "AS","AND","OR","NOT","IN","IS","NULL","PRIMARY","KEY","IF","EXISTS"
].map((k) => ({ label: k, type: "keyword" as const }));

interface Candidate {
  label: string;
  type: "table" | "field" | "keyword";
}

/**
 * Hook: returns CodeMirror extensions for DuckDB‐powered SQL autocomplete.
 * Uses conn.tokenize(sql) to surface every identifier in the current buffer
 * (tables, columns, etc.), plus static lists for tables and keywords.
 */
export function useSqlAutocomplete(conn: AsyncDuckDBConnection | null) {
  const [extensions, setExtensions] = useState<any[]>([sqlLang()]);

  useEffect(() => {
    if (!conn) return;
    let cancelled = false;

    async function build() {
      // 1. Fetch all tables once
      const tables = await listRelations(conn);
      const tableCandidates: Candidate[] = tables.map((t) => ({
        label: t,
        type: "table",
      }));

      if (cancelled) return;

      // 2. Install a single autocompletion override
      const completionExt = autocompletion({
        override: [
          async (context: CompletionContext): Promise<CompletionResult | null> => {
            // Only trigger inside word characters
            const before = context.matchBefore(/\w*/);
            if (!before || (before.from === before.to && !context.explicit)) {
              return null;
            }

            // Get the full document so far
            const fullDoc = context.state.doc.toString();

            // Tokenize via DuckDB
            const tokens: SQLToken[] = await (conn as any).tokenize(fullDoc);

            // Pull out every distinct identifier we saw
            const fields = Array.from(
              new Set(
                tokens
                  .filter((t) => t.type === "identifier")
                  .map((t) => t.value)
              )
            ).map((f) => ({ label: f, type: "field" as const }));

            // Build one combined list
            const list: Candidate[] = [
              ...KEYWORDS,
              ...tableCandidates,
              ...fields,
            ];

            // Delegate to CodeMirror's completeFromList
            return completeFromList(
              list.map((c) => ({
                label: c.label,
                type: c.type,
              }))
            )(context) as CompletionResult;
          },
        ],
      });

      // Update our extensions
      setExtensions([sqlLang(), completionExt]);
    }

    build();
    return () => {
      cancelled = true;
    };
  }, [conn]);

  return extensions;
}
